import { ADDABLE_ARTIFACTS } from "../constants/credits";
import { storageGet, storageRemove, storageSet } from "./storage";

/** @typedef {{detailFixes: Record<string,string>, template: string, artifacts: string[], resumeRequested: boolean}} ConfirmationChoices */
/** @typedef {ConfirmationChoices & {schema: 1, id: string, uuid: string, savedAt: number, persisted: boolean}} ConfirmationDraft */
const RESUME_KEY = "liq_resume_checkout";
const MAX_LENGTH = 12000;
const TEXT_LIMITS = { street_address: 255, city: 100, address_state: 2, zipcode: 10 };
const NUMERIC_FIELDS = ["bedrooms", "bathrooms", "living_area", "lot_size", "price"];
const FIELDS = [...Object.keys(TEXT_LIMITS), ...NUMERIC_FIELDS];
/** @type {Map<string, ConfirmationDraft>} */
const memory = new Map();
const cleared = new Set();
/** @type {Map<string, Set<string>>} */
const superseded = new Map();
/** @param {string} value */
const hasControls = (value) =>
  Array.from(value).some(
    (character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127
  );
let sequence = 0;
/** @param {string} uuid */
const key = (uuid) => `liq_confirmation_draft:${uuid}`;
/** @param {any} value */
const object = (value) => !!value && typeof value === "object" && !Array.isArray(value);

/** Numeric values are compared canonically after the server's harmless formatting. */
/** @param {string} field @param {unknown} value @param {boolean} [strict] */
const numeric = (field, value, strict = false) => {
  const raw = String(value ?? "")
    .replaceAll(",", "")
    .replace(field === "price" ? /^\$/ : /$^/, "")
    .trim();
  if (!/^\d+(?:\.\d+)?$/.test(raw)) return null;
  const number = Number(raw);
  if (!Number.isFinite(number)) return null;
  if (["bedrooms", "bathrooms"].includes(field))
    return number >= 0 &&
      number <= 50 &&
      (!strict || /^\d+(?:\.\d)?$/.test(raw)) &&
      Number.isInteger(number * 10)
      ? number
      : null;
  if (field === "living_area")
    return Number.isInteger(number) && number >= 1 && number <= 1000000 ? number : null;
  if (field === "lot_size")
    return Number.isInteger(number) && number >= 1 && number <= 100000000 ? number : null;
  return number >= 1000 && number <= 1000000000 ? Math.round(number * 100) / 100 : null;
};

/** Reject malformed choices rather than silently dropping a user's corrections. */
/** @param {any} value @param {{allowIncomplete?: boolean}} [options] @returns {ConfirmationChoices | null} */
export const validateConfirmationChoices = (value, { allowIncomplete = false } = {}) => {
  if (
    !object(value) ||
    !object(value.detailFixes) ||
    typeof value.template !== "string" ||
    value.template.length > 120 ||
    hasControls(value.template) ||
    !Array.isArray(value.artifacts) ||
    value.artifacts.some((/** @type {any} */ item) => !ADDABLE_ARTIFACTS.includes(item))
  )
    return null;
  /** @type {Record<string,string>} */
  const detailFixes = {};
  for (const [field, raw] of Object.entries(value.detailFixes)) {
    if (!FIELDS.includes(field) || typeof raw !== "string" || raw.length > 300 || hasControls(raw))
      return null;
    const text = raw.trim();
    if (!text) {
      detailFixes[field] = "";
      continue;
    }
    if (allowIncomplete) {
      detailFixes[field] = raw;
      continue;
    }
    if (NUMERIC_FIELDS.includes(field)) {
      if (numeric(field, text, true) === null) return null;
    } else if (text.length > TEXT_LIMITS[/** @type {keyof typeof TEXT_LIMITS} */ (field)])
      return null;
    detailFixes[field] = text;
  }
  return {
    detailFixes,
    template: value.template,
    artifacts: [...new Set(/** @type {string[]} */ (value.artifacts))],
    resumeRequested: value.resumeRequested === true,
  };
};

/** @param {string | null} raw @param {string} uuid @param {boolean} [legacy] @returns {ConfirmationDraft | null} */
const decode = (raw, uuid, legacy = false) => {
  if (!raw || raw.length > MAX_LENGTH) return null;
  try {
    const record = JSON.parse(raw);
    if (!object(record) || record.uuid !== uuid) return null;
    const choices = validateConfirmationChoices(
      {
        ...record,
        detailFixes: record.detailFixes ?? {},
        template: record.template ?? "",
        artifacts: record.artifacts ?? [],
        resumeRequested: legacy && record.schema !== 1 ? true : record.resumeRequested,
      },
      { allowIncomplete: true }
    );
    if (!choices) return null;
    if (
      record.schema === 1 &&
      (typeof record.id !== "string" || !record.id || !Number.isFinite(record.savedAt))
    )
      return null;
    if (record.schema !== 1 && (!legacy || record.schema !== undefined)) return null;
    return {
      ...choices,
      schema: 1,
      id: record.id || `legacy:${raw}`,
      uuid,
      savedAt: record.savedAt || 0,
      persisted: true,
    };
  } catch {
    return null;
  }
};

/** Load only for the authorized listing; another listing's marker is never adopted. */
/** @param {string} uuid @returns {ConfirmationDraft | null} */
export const loadConfirmationDraft = (uuid) => {
  if (!uuid) return null;
  if (memory.has(uuid)) return memory.get(uuid) || null;
  const draft = decode(storageGet(key(uuid)), uuid) || decode(storageGet(RESUME_KEY), uuid, true);
  return draft && !cleared.has(draft.id) ? draft : null;
};

/** Persist synchronously before navigation or a paid action; failed storage remains tab-local only. */
/** @param {string} uuid @param {ConfirmationChoices} value @returns {ConfirmationDraft | null} */
export const saveConfirmationDraft = (uuid, value) => {
  const choices = validateConfirmationChoices(value, { allowIncomplete: true });
  if (!uuid || !choices) return null;
  const draft = /** @type {ConfirmationDraft} */ ({
    ...choices,
    schema: 1,
    uuid,
    id: `${Date.now().toString(36)}-${(++sequence).toString(36)}-${Math.random().toString(36).slice(2)}`,
    savedAt: Date.now(),
    persisted: true,
  });
  const serialized = JSON.stringify(draft);
  if (serialized.length > MAX_LENGTH) return null;
  const previousStored = decode(storageGet(key(uuid)), uuid);
  const previousMarker = decode(storageGet(RESUME_KEY), uuid, true);
  const oldIds = new Set(superseded.get(uuid) || []);
  if (previousStored) oldIds.add(previousStored.id);
  if (previousMarker) oldIds.add(previousMarker.id);
  storageSet(key(uuid), serialized);
  const mirrorResume = draft.resumeRequested || !!previousMarker;
  if (mirrorResume) storageSet(RESUME_KEY, serialized);
  draft.persisted =
    storageGet(key(uuid)) === serialized &&
    (!mirrorResume || storageGet(RESUME_KEY) === serialized);
  if (draft.persisted) {
    memory.delete(uuid);
    superseded.delete(uuid);
  } else {
    memory.set(uuid, draft);
    superseded.set(uuid, oldIds);
  }
  return draft;
};

/** A late acknowledgment cannot erase later choices, even if storage removal fails. */
/** @param {string} uuid @param {string | null | undefined} expectedId */
export const clearConfirmationDraft = (uuid, expectedId) => {
  const current = loadConfirmationDraft(uuid);
  if (!expectedId || current?.id !== expectedId) return false;
  const ownedIds = new Set([expectedId, ...(superseded.get(uuid) || [])]);
  const marker = decode(storageGet(RESUME_KEY), uuid, true);
  const stored = decode(storageGet(key(uuid)), uuid);
  if (marker && ownedIds.has(marker.id)) storageRemove(RESUME_KEY);
  if (stored && ownedIds.has(stored.id)) storageRemove(key(uuid));
  for (const id of ownedIds) cleared.add(id);
  memory.delete(uuid);
  superseded.delete(uuid);
  return true;
};

/** @param {any} value */
export const isMissingListingFact = (value) =>
  value === null || value === undefined || value === "";

/** Only missing facts can be filled; imported zero remains authoritative. */
/** @param {any} listing @param {Record<string,string>} fixes */
export const pendingGapFills = (listing, fixes) =>
  Object.fromEntries(
    Object.entries(fixes).filter(
      ([field, value]) =>
        FIELDS.includes(field) && value.trim() && isMissingListingFact(listing?.[field])
    )
  );

/** A confirmation flag alone does not prove that this attempt's new facts landed. */
/** @param {any} listing @param {Record<string,string>} fixes */
export const confirmedGapFillsMatch = (listing, fixes) =>
  Object.entries(fixes).every(([field, requested]) => {
    const actual = listing?.[field];
    if (isMissingListingFact(actual)) return false;
    return NUMERIC_FIELDS.includes(field)
      ? numeric(field, actual) !== null && numeric(field, actual) === numeric(field, requested)
      : String(actual).trim() === requested.trim();
  });
